import React from 'react';
import { Platform, Pressable, View, Text } from 'react-native';
import { Dialog, XStack, YStack } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface FeatureUpgradeModalProps {
  open: boolean;
  onClose: () => void;
  onUpgrade?: () => void;
  features?: string[];
  planName?: string;
  planPrice?: string;
  planDescription?: string;
}

const FeatureUpgradeModal: React.FC<FeatureUpgradeModalProps> = ({
  open,
  onClose,
  onUpgrade,
  features = [
    'Advanced Analytics & Reporting',
    'Priority Customer Support',
    'Custom Integrations',
    'Unlimited API Access',
    'Team Collaboration Tools',
    'Advanced Security Features',
  ],
  planName = 'Premium Plan',
  planPrice = '$29/mo',
  planDescription = 'Starting at $29/month'
}) => {
  const theme = themeSelector();

  return (
    <Dialog key="featureUpgrade" open={open}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              minWidth: 500,
              maxWidth: 600,
              maxHeight: "85%",
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" gap="$3">
              <View style={{
                backgroundColor: theme ? "#d1fae5" : "#064e3b",
                borderRadius: 12,
                padding: 10,
              }}>
                <MaterialIcons
                  name="trending-up"
                  size={20}
                  color={theme ? "#059669" : "#10b981"}
                />
              </View>
              <YStack flex={1}>
                <Dialog.Title style={{
                  fontSize: 20,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 4,
                  margin: 0,
                }}>
                  Unlock Premium Features
                </Dialog.Title>
                <Dialog.Description style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontWeight: '500',
                  margin: 0,
                }}>
                  Upgrade to access advanced capabilities
                </Dialog.Description>
              </YStack>
            </XStack>
          </View>

          {/* Content */}
          <View style={{ padding: 24 }}>
            <YStack gap="$4">
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}>
                <YStack gap="$3">
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 8,
                  }}>
                    This feature requires a Premium plan
                  </Text>
                  <Text style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    lineHeight: 22,
                  }}>
                    Upgrade now to unlock powerful features including advanced analytics, priority support, custom integrations, and more.
                  </Text>
                </YStack>
              </View>

              {/* Feature List */}
              <YStack gap="$2">
                {features.map((feature, index) => (
                  <XStack key={index} alignItems="center" gap="$2">
                    <MaterialIcons
                      name="check-circle"
                      size={20}
                      color={theme ? "#059669" : "#10b981"}
                    />
                    <Text style={{
                      fontSize: 14,
                      color: theme ? '#1e293b' : '#f1f5f9',
                    }}>
                      {feature}
                    </Text>
                  </XStack>
                ))}
              </YStack>

              {/* Pricing Card */}
              <View style={{
                backgroundColor: theme ? '#eff6ff' : '#1e3a5f',
                borderRadius: 12,
                padding: 16,
                borderWidth: 2,
                borderColor: theme ? '#3b82f6' : '#60a5fa',
              }}>
                <XStack alignItems="center" justifyContent="space-between">
                  <YStack>
                    <Text style={{
                      fontSize: 18,
                      fontWeight: '700',
                      color: theme ? '#1e293b' : '#f1f5f9',
                    }}>
                      {planName}
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? '#64748b' : '#94a3b8',
                      marginTop: 4,
                    }}>
                      {planDescription}
                    </Text>
                  </YStack>
                  <Text style={{
                    fontSize: 24,
                    fontWeight: '800',
                    color: theme ? '#3b82f6' : '#60a5fa',
                  }}>
                    {planPrice}
                  </Text>
                </XStack>
              </View>
            </YStack>
          </View>

          {/* Footer Actions */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack gap="$3" justifyContent="flex-end">
              <Dialog.Close asChild>
                <Pressable
                  onPress={onClose}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Maybe Later
                  </Text>
                </Pressable>
              </Dialog.Close>
              <Dialog.Close asChild>
                <Pressable
                  onPress={() => {
                    onClose();
                    onUpgrade?.();
                  }}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#059669' : '#10b981',
                    minWidth: 100,
                    alignItems: 'center',
                    elevation: 2,
                    shadowColor: '#059669',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Upgrade Now
                  </Text>
                </Pressable>
              </Dialog.Close>
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default FeatureUpgradeModal;

